import React, { useState, useRef, useCallback } from 'react';
import { X, RotateCw, Check } from 'lucide-react';

interface ImageCropUploaderProps {
  selectedImage: string; // Pre-selected image data URL
  onImageCropped: (croppedImage: Blob, previewUrl: string) => void;
  onCancel: () => void;
  aspectRatio?: number; // width / height (e.g., 1 for 1:1, 16/9 for 16:9)
  title?: string;
  maxSizeKB?: number;
}

export const ImageCropUploader: React.FC<ImageCropUploaderProps> = ({
  selectedImage,
  onImageCropped,
  onCancel,
  aspectRatio = 1, // Default 1:1
  title = 'Crop Image',
  maxSizeKB = 5120 // Default 5MB
}) => {
  const [frameScale, setFrameScale] = useState(1); // Scale of the frame
  const [rotation, setRotation] = useState(0);
  const [framePosition, setFramePosition] = useState({ x: 0, y: 0 }); // Position of frame
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeCorner, setResizeCorner] = useState<'tl' | 'tr' | 'bl' | 'br' | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ width: 0, height: 0, x: 0, y: 0, posX: 0, posY: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);

  const handleImageLoad = () => {
    if (imageRef.current && containerRef.current) {
      const natWidth = imageRef.current.naturalWidth;
      const natHeight = imageRef.current.naturalHeight;
      
      setImageSize({ width: natWidth, height: natHeight });
      
      // Calculate display size to fit container
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      
      const scale = Math.min(
        containerWidth / natWidth,
        containerHeight / natHeight,
        1 // Don't upscale
      );
      
      setDisplaySize({
        width: natWidth * scale,
        height: natHeight * scale
      });
      
      // Reset frame position to center
      setFramePosition({ x: 0, y: 0 });
      setFrameScale(1);
    }
  };

  const handleFrameMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - framePosition.x,
      y: e.clientY - framePosition.y
    });
  };

  const handleCornerMouseDown = (e: React.MouseEvent, corner: 'tl' | 'tr' | 'bl' | 'br') => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeCorner(corner);
    
    const frame = getCropFrame();
    setResizeStart({
      width: frame.width,
      height: frame.height,
      x: e.clientX,
      y: e.clientY,
      posX: framePosition.x,
      posY: framePosition.y
    });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging && containerRef.current && frameRef.current) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      // Get container and frame dimensions
      const containerRect = containerRef.current.getBoundingClientRect();
      const frameRect = frameRef.current.getBoundingClientRect();
      
      // Calculate bounds to keep frame within container
      const maxX = (containerRect.width - frameRect.width) / 2;
      const maxY = (containerRect.height - frameRect.height) / 2;
      const minX = -maxX;
      const minY = -maxY;
      
      setFramePosition({
        x: Math.max(minX, Math.min(maxX, newX)),
        y: Math.max(minY, Math.min(maxY, newY))
      });
    }
    
    if (isResizing && containerRef.current && resizeCorner) {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      
      let newWidth = resizeStart.width;
      let newX = resizeStart.posX;
      let newY = resizeStart.posY;
      
      // Calculate new dimensions based on corner being dragged
      switch (resizeCorner) {
        case 'br': // Bottom-right
          newWidth = resizeStart.width + deltaX;
          break;
        case 'bl': // Bottom-left
          newWidth = resizeStart.width - deltaX;
          newX = resizeStart.posX + deltaX / 2;
          break;
        case 'tr': // Top-right
          newWidth = resizeStart.width + deltaX;
          newY = resizeStart.posY + deltaY / 2;
          break;
        case 'tl': // Top-left
          newWidth = resizeStart.width - deltaX;
          newX = resizeStart.posX + deltaX / 2;
          newY = resizeStart.posY + deltaY / 2;
          break;
      }
      
      // Enforce minimum and maximum sizes
      const containerRect = containerRef.current.getBoundingClientRect();
      const minSize = 50;
      const maxSize = Math.min(containerRect.width * 0.9, containerRect.height * 0.9 * aspectRatio);
      
      newWidth = Math.max(minSize, Math.min(maxSize, newWidth));
      
      // Calculate base frame size to determine scale
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      let baseWidth = Math.min(containerWidth * 0.6, 300);
      let baseHeight = baseWidth / aspectRatio;
      if (baseHeight > containerHeight * 0.6) {
        baseHeight = containerHeight * 0.6;
        baseWidth = baseHeight * aspectRatio;
      }
      
      const newScale = newWidth / baseWidth;
      setFrameScale(newScale);
      setFramePosition({ x: newX, y: newY });
    }
  }, [isDragging, isResizing, dragStart, resizeStart, resizeCorner, aspectRatio]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeCorner(null);
  }, []);

  React.useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const getCropFrame = () => {
    if (!containerRef.current) return { width: 200, height: 200 };
    
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    
    // Base frame size (before scaling)
    let baseWidth = Math.min(containerWidth * 0.6, 300);
    let baseHeight = baseWidth / aspectRatio;
    
    // If height exceeds container, scale down
    if (baseHeight > containerHeight * 0.6) {
      baseHeight = containerHeight * 0.6;
      baseWidth = baseHeight * aspectRatio;
    }
    
    // Apply frame scale
    return { 
      width: baseWidth * frameScale, 
      height: baseHeight * frameScale 
    };
  };

  const handleCrop = async () => {
    if (!imageRef.current || !canvasRef.current || !containerRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const frame = getCropFrame();
    const containerRect = containerRef.current.getBoundingClientRect();

    // Calculate frame position in container coordinates
    const frameCenterX = containerRect.width / 2 + framePosition.x;
    const frameCenterY = containerRect.height / 2 + framePosition.y;
    const frameLeft = frameCenterX - frame.width / 2;
    const frameTop = frameCenterY - frame.height / 2;

    // Calculate image position in container (centered)
    const imageLeft = (containerRect.width - displaySize.width) / 2;
    const imageTop = (containerRect.height - displaySize.height) / 2;

    // Calculate crop area relative to displayed image
    const cropX = (frameLeft - imageLeft) / displaySize.width * imageSize.width;
    const cropY = (frameTop - imageTop) / displaySize.height * imageSize.height;
    const cropWidth = (frame.width / displaySize.width) * imageSize.width;
    const cropHeight = (frame.height / displaySize.height) * imageSize.height;

    // Set canvas size to desired output size
    const outputSize = 512; // High quality output
    canvas.width = outputSize;
    canvas.height = outputSize / aspectRatio;

    // Draw the cropped image
    ctx.save();
    
    // Apply rotation if needed
    if (rotation !== 0) {
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-canvas.width / 2, -canvas.height / 2);
    }

    ctx.drawImage(
      imageRef.current,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );
    
    ctx.restore();

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (blob) {
        const previewUrl = URL.createObjectURL(blob);
        onImageCropped(blob, previewUrl);
      }
    }, 'image/jpeg', 0.95);
  };

  const frame = getCropFrame();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <button
            onClick={onCancel}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Crop Area */}
            <div 
              ref={containerRef}
              className="flex-1 relative overflow-hidden bg-gray-900 flex items-center justify-center"
              style={{ minHeight: '400px' }}
            >
              {/* Image - Fixed/Centered */}
              <img
                ref={imageRef}
                src={selectedImage}
                alt="Crop preview"
                onLoad={handleImageLoad}
                className="select-none"
                style={{
                  width: `${displaySize.width}px`,
                  height: `${displaySize.height}px`,
                  transform: `rotate(${rotation}deg)`,
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
                draggable={false}
              />

              {/* Crop Frame Overlay */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Dark overlay */}
                <div className="absolute inset-0 bg-black opacity-50" />
                
                {/* Movable crop frame */}
                <div
                  ref={frameRef}
                  onMouseDown={handleFrameMouseDown}
                  className="absolute bg-transparent border-2 border-white shadow-lg pointer-events-auto cursor-move"
                  style={{
                    width: `${frame.width}px`,
                    height: `${frame.height}px`,
                    left: '50%',
                    top: '50%',
                    transform: `translate(calc(-50% + ${framePosition.x}px), calc(-50% + ${framePosition.y}px))`,
                    mixBlendMode: 'normal',
                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  {/* Rule of thirds grid lines */}
                  <div className="absolute inset-0 pointer-events-none">
                    {/* Vertical lines */}
                    <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white opacity-30" />
                    <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white opacity-30" />
                    {/* Horizontal lines */}
                    <div className="absolute top-1/3 left-0 right-0 h-px bg-white opacity-30" />
                    <div className="absolute top-2/3 left-0 right-0 h-px bg-white opacity-30" />
                  </div>

                  {/* Corner resize handles */}
                  <div
                    onMouseDown={(e) => handleCornerMouseDown(e, 'tl')}
                    className="absolute -top-2 -left-2 w-6 h-6 bg-white border-2 border-accent-500 rounded-full cursor-nw-resize hover:scale-110 transition-transform z-10"
                    title="Drag to resize"
                  />
                  <div
                    onMouseDown={(e) => handleCornerMouseDown(e, 'tr')}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-white border-2 border-accent-500 rounded-full cursor-ne-resize hover:scale-110 transition-transform z-10"
                    title="Drag to resize"
                  />
                  <div
                    onMouseDown={(e) => handleCornerMouseDown(e, 'bl')}
                    className="absolute -bottom-2 -left-2 w-6 h-6 bg-white border-2 border-accent-500 rounded-full cursor-sw-resize hover:scale-110 transition-transform z-10"
                    title="Drag to resize"
                  />
                  <div
                    onMouseDown={(e) => handleCornerMouseDown(e, 'br')}
                    className="absolute -bottom-2 -right-2 w-6 h-6 bg-white border-2 border-accent-500 rounded-full cursor-se-resize hover:scale-110 transition-transform z-10"
                    title="Drag to resize"
                  />
                </div>
              </div>

              {/* Hidden canvas for cropping */}
              <canvas ref={canvasRef} className="hidden" />
            </div>

            {/* Controls */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <div className="flex items-center justify-between gap-4">
                {/* Rotate Control */}
                <button
                  onClick={() => setRotation((rotation + 90) % 360)}
                  className="p-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Rotate 90°"
                >
                  <RotateCw className="w-5 h-5" />
                </button>

                {/* Action Buttons */}
                <div className="flex space-x-2 ml-auto">
                  <button
                    onClick={onCancel}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg transition-colors flex items-center"
                  >
                    <X className="w-5 h-5 mr-2" />
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={handleCrop}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                  >
                    <Check className="w-5 h-5" />
                    <span>Save</span>
                  </button>
                </div>
              </div>

              {/* Instructions */}
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-3 text-center">
                Drag frame to move • Drag corners to resize • Rotate as needed
              </p>
            </div>
        </div>
      </div>
    </div>
  );
};

